import { useState, useContext } from 'react'
import { GlobalContext } from '../../state'
import { useNavigate } from 'react-router-dom'
import useLogin from '../../hooks/useLogin'
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Stack,
  useTheme
} from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'

function LoginForm({ }) {
  const { globalState, setGlobalState } = useContext(GlobalContext);
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const navigate = useNavigate()
  const attemptLogin = useLogin()
  const [ErrorMessages, setErrorMessages] = useState([])

  // Theme & Darkmode
  const theme = useTheme()
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)')

  const onUsernameChange = (event) => setUsername(event.target.value.toLowerCase())
  const onPasswordChange = (event) => setPassword(event.target.value)
  const onLoginSubmit = (event) => {
    event.preventDefault()
    const loginData = { username, password }
    attemptLogin(loginData)
      .then((res) => {
        if (!res || res === false) {
          setErrorMessages(['Wrong username or password!'])
          return
        }
        if (res.status === 'ok') {
          localStorage.setItem('token', res.data.token)
          localStorage.setItem('rights', JSON.stringify(res.data.rights))
          setGlobalState({
            ...globalState,
            token: res.data.token,
            rights: res.data.rights
          });

          // Navigate to dashboard with reload
          navigate('/dashboard', { replace: true })

        } else {
          setErrorMessages([res.message || 'Login error!'])
        }
      })
      .catch((err) => setErrorMessages([err.message || 'Unexpected error during login!']))
  }

  // Hilfsfunktion base64url → Uint8Array
  function base64urlToUint8Array(base64url) {
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4 ? 4 - (base64.length % 4) : 0;
    const base64Padded = base64 + '='.repeat(pad);
    const str = atob(base64Padded);
    return Uint8Array.from([...str].map(c => c.charCodeAt(0)));
  }

// Hilfsfunktion Uint8Array → base64url
  function uint8ToBase64url(u8) {
    return btoa(String.fromCharCode(...u8))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  const handlePasskeyLogin = async () => {
    setErrorMessages([]);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URI}/login/passkey/login-options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({  }),
      });
      const options = await res.json();
      if (options.status !== 'ok') throw new Error(options.message);

      // challenge und allowCredentials.id umwandeln
      const publicKey = options.data;
      publicKey.challenge = base64urlToUint8Array(publicKey.challenge);
      if (publicKey.allowCredentials) {
        publicKey.allowCredentials = publicKey.allowCredentials.map(cred => ({
          ...cred,
          id: base64urlToUint8Array(cred.id),
        }));
      }

      const assertion = await navigator.credentials.get({ publicKey });

      // Antwort für die API vorbereiten
      const credential = {

        id: assertion.id,
        rawId: uint8ToBase64url(new Uint8Array(assertion.rawId)),
        type: assertion.type,
        response: {
          authenticatorData: uint8ToBase64url(new Uint8Array(assertion.response.authenticatorData)),
          clientDataJSON: uint8ToBase64url(new Uint8Array(assertion.response.clientDataJSON)),
          signature: uint8ToBase64url(new Uint8Array(assertion.response.signature)),
          userHandle: assertion.response.userHandle
            ? uint8ToBase64url(new Uint8Array(assertion.response.userHandle))
            : null,
      },
      };

      const verifyRes = await fetch(`${process.env.REACT_APP_API_URI}/login/passkey/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challengeId: publicKey.challengeId, assertion: credential }),
      });
      const verifyData = await verifyRes.json();
      if (verifyData.status === 'ok') {
        localStorage.setItem('token', verifyData.data.token);
        localStorage.setItem('rights', JSON.stringify(verifyData.data.rights));
        setGlobalState({
          ...globalState,
          token: verifyData.data.token,
          rights: verifyData.data.rights
        });
        navigate('/dashboard');
      } else {
        setErrorMessages([verifyData.message || 'Passkey-Login error!']);
      }
    } catch (e) {
      setErrorMessages([e.message || 'Passkey-Login error!']);
    }
  };

  if (ErrorMessages.length > 0) {
    setTimeout(() => setErrorMessages([]), 5000)
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: prefersDarkMode
          ? 'linear-gradient(135deg, #23272f 0%, #2c313a 100%)'
          : 'linear-gradient(135deg, #ece9f7 0%, #cfd9df 100%)'
      }}
    >
      <Paper
        elevation={6}
        sx={{
          p: 4,
          minWidth: 350,
          maxWidth: 400,
          bgcolor: prefersDarkMode ? theme.palette.background.default : '#86899f',
          color: prefersDarkMode ? theme.palette.text.primary : 'inherit'
        }}
      >
        <Typography variant="h5" align="center" gutterBottom>
          Login
        </Typography>
        <form onSubmit={onLoginSubmit}>
          <Stack spacing={2}>
            {ErrorMessages.length > 0 && (
              <Alert severity="error">
                {ErrorMessages.map((msg) => (
                  <div key={msg}>{msg}</div>
                ))}
              </Alert>
            )}
            <TextField
              label="E-Mail"
              type="email"
              value={username}
              onChange={onUsernameChange}
              autoComplete="username"
              required
              fullWidth
              variant="filled"
              InputProps={{
                style: prefersDarkMode
                  ? { backgroundColor: '#23272f', color: '#fff' }
                  : {}
              }}
              InputLabelProps={{
                style: prefersDarkMode
                  ? { color: '#bbb' }
                  : { color: '#444' }
              }}
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={onPasswordChange}
              autoComplete="current-password"
              required
              fullWidth
              variant="filled"
              InputProps={{
                style: prefersDarkMode
                  ? { backgroundColor: '#23272f', color: '#fff' }
                  : {}
              }}
              InputLabelProps={{
                style: prefersDarkMode
                  ? { color: '#bbb' }
                  : { color: '#444' }
              }}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 1 }}
            >
              Login
            </Button>
            <Button
              type="button"
              variant="outlined"
              color="secondary"
              onClick={handlePasskeyLogin}
              fullWidth
            >
              Passkey Login
            </Button>
          </Stack>
        </form>
      </Paper>
    </Box>
  )
}

export default LoginForm
